import { AnyAction, createSlice } from '@reduxjs/toolkit';

import { ApiStatus } from '@constants';
import { setImpersonation } from '@redux/accounts';
import { writeErrorFrom } from '@redux/candidateDetails';
import { availableCandidatesStoreKey } from './availableCandidates.const';

import { IAvailableCandidatesSliceState } from './availableCandidates.types';
import {
  fetchAvailableCandidates,
  fetchAvailableCandidatesCount,
  requestAvailableCandidateInterview,
  requestAvailableCandidateLearnMore,
  markAvailableCandidate,
} from './availableCandidates.thunk';

const initialState: IAvailableCandidatesSliceState = {
  apiStatus: ApiStatus.IDLE,
  requestId: null,
  data: [],
  count: null,
  writeStatus: ApiStatus.IDLE,
  writeError: null,
  writing: [],
  marks: {},
};

const ALREADY_ANSWERED =
  'This candidate was already answered. Reload to see the latest.';

const without = (candidateIds: string[], action: AnyAction) =>
  candidateIds.filter(
    (candidateId) => candidateId !== action.meta.arg.candidateId,
  );

const doneWriting = (
  state: IAvailableCandidatesSliceState,
  action: AnyAction,
) => {
  state.writing = without(state.writing, action);
};

const undoMark = (state: IAvailableCandidatesSliceState, action: AnyAction) => {
  delete state.marks[action.meta.arg.candidateId];
};

const writes = [
  requestAvailableCandidateInterview,
  requestAvailableCandidateLearnMore,
];

const availableCandidatesSlice = createSlice({
  name: availableCandidatesStoreKey,
  initialState,
  reducers: {
    clearAvailableCandidateWriteError: (state) => {
      state.writeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableCandidates.pending, (state, action) => {
        state.apiStatus = ApiStatus.LOADING;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchAvailableCandidates.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.apiStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        fetchAvailableCandidates.rejected,
        (state, action: AnyAction) => {
          if (action.meta.requestId !== state.requestId) return;
          state.apiStatus =
            action?.payload?.statusCode === 403
              ? ApiStatus.FORBIDDEN
              : ApiStatus.FAILED;
        },
      )
      .addCase(fetchAvailableCandidatesCount.fulfilled, (state, action) => {
        state.count = action.payload.count;
      })
      .addCase(markAvailableCandidate.pending, (state, action) => {
        state.writing.push(action.meta.arg.candidateId);
        state.marks[action.meta.arg.candidateId] = action.meta.arg.mark;
      })
      .addCase(markAvailableCandidate.fulfilled, (state, action) => {
        doneWriting(state, action);
        if (!action.payload) undoMark(state, action);
      })
      .addCase(markAvailableCandidate.rejected, (state, action) => {
        doneWriting(state, action);
        undoMark(state, action);
      })
      .addCase(setImpersonation.pending, () => ({
        ...initialState,
        apiStatus: ApiStatus.LOADING,
      }));

    writes.forEach((write) =>
      builder
        .addCase(write.pending, (state, action: AnyAction) => {
          state.writeStatus = ApiStatus.LOADING;
          state.writeError = null;
          state.writing.push(action.meta.arg.candidateId);
        })
        .addCase(write.fulfilled, (state, action: AnyAction) => {
          state.writeStatus = ApiStatus.COMPLETE;
          doneWriting(state, action);
          if (action.payload === false) state.writeError = ALREADY_ANSWERED;
        })
        .addCase(write.rejected, (state, action: AnyAction) => {
          state.writeStatus = ApiStatus.FAILED;
          doneWriting(state, action);
          state.writeError = writeErrorFrom(action.payload, ALREADY_ANSWERED);
        }),
    );
  },
});

export const { clearAvailableCandidateWriteError } =
  availableCandidatesSlice.actions;
export const availableCandidatesSliceReducer = availableCandidatesSlice.reducer;
